import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { EnquiryAttribution } from '@theme/enquiry-attribution';
import { submitEnquiry, isTestSubmission } from '@theme/enquiry-transport';
import { reportEnquiryConversion } from '@theme/enquiry-conversion';
import { push, EVENTS } from '@theme/analytics';

/**
 * The step engine behind all four enquiry routes.
 *
 * One component with four configurations, not four forms. Routes differ in which step blocks the
 * merchant places, never in code. Step content, copy and order live in block schema so they stay
 * editable in the Customizer; branch logic, validation and analytics event names do not, because
 * those break silently when edited.
 *
 * Validation follows the model in gift-card-recipient-form.js — per-field error containers, a live
 * region for assistive technology, `aria-invalid` and `aria-describedby` wiring. The one thing
 * added here is **scrolling the error into view**, which is the specific bug being fixed: the
 * current form renders its error 221px above the top of the viewport and never scrolls to it, so
 * the only visible feedback is the submit button fading. Around 81 fully-qualified people a
 * quarter abandon there.
 *
 * Error copy is derived from the field's own label rather than written per-message, which makes
 * "Company Name" saying "Please enter your last name" structurally impossible.
 *
 * @see docs/build-brief-lead-capture.md — §4, §7, §9
 */

/**
 * Screen one's answer maps to a branch. Not merchant-editable: brief §8 lists branch logic as
 * not editable, because a typo here silently sends everyone down one path.
 *
 * @type {Record<string, string>}
 */
const BRANCH_KEYS = {
  knows_what_they_want: 'a',
  needs_ideas: 'b',
};

/** Minimum time on the form before a submission is believable, in milliseconds. */
const MIN_COMPLETION_MS = 3000;

/**
 * @typedef {Object} EnquiryFlowRefs
 * @property {HTMLElement[]} steps - Question steps, in document order
 * @property {HTMLElement} [confirmation] - Shown on success
 * @property {HTMLElement} [failure] - Shown when a submission could not be delivered
 * @property {HTMLElement} [summary] - Populated with a restatement of the answers
 * @property {HTMLElement} [liveRegion] - Screen reader announcements
 * @property {HTMLElement} [progressBar]
 * @property {HTMLElement} [progressText]
 * @property {HTMLButtonElement} [backButton]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLElement} [controls]
 * @property {HTMLInputElement} [honeypot]
 * @property {HTMLAnchorElement} [mailtoFallback]
 */

/**
 * @extends {Component<EnquiryFlowRefs>}
 */
class EnquiryFlow extends Component {
  requiredRefs = ['steps'];

  /** @type {number} Index into the currently visible steps */
  #stepIndex = 0;

  /** @type {string | null} 'a' | 'b' | null before screen one is answered */
  #branch = null;

  /** @type {Record<string, string>} Collected answers, keyed by HubSpot property name */
  #answers = {};

  /** @type {number} */
  #startedAt = 0;

  /** @type {number} */
  #stepEnteredAt = 0;

  /** @type {boolean} */
  #submitting = false;

  /** Fields the visitor has answered themselves. Pre-selection never overrides these. */
  #touched = new Set();

  /**
   * True once the branch has been answered from behaviour rather than asked.
   *
   * When set, screen one is lifted out of the step sequence and shown as a persistent line, which
   * is what lands Branch A on the four steps the brief promises rather than five. `changeBranch()`
   * puts it back.
   */
  #branchAbsorbed = false;

  /**
   * How screen one was answered: `inferred` from the shortlist, or `asked`. Sent as
   * `enquiry_branch_source`, because the branch is the value HubSpot triage runs on and a guess
   * should never be indistinguishable from an answer.
   *
   * @type {'asked' | 'inferred' | null}
   */
  #branchSource = null;

  /**
   * Quantities typed in this flow, keyed by variant ID. Held separately from the store until the
   * step is left, so a half-typed number never reaches storage.
   *
   * @type {Map<string, string>}
   */
  #quantityDrafts = new Map();

  /**
   * Variant IDs whose quantity was filled in from the headcount rather than typed. They follow the
   * headcount if it changes; a typed quantity never does.
   *
   * @type {Set<string>}
   */
  #quantitySeeded = new Set();

  /** @type {(() => void) | null} */
  #onShortlistChange = null;

  connectedCallback() {
    super.connectedCallback();

    this.#startedAt = Date.now();
    this.#stepEnteredAt = this.#startedAt;

    EnquiryAttribution.capture();

    this.#bindOptionInputs();
    this.#render();

    // `isTrusted` is the only reliable way to tell a real interaction from the synthetic `change`
    // that `preselect()` dispatches — without it, pre-selecting a field would immediately mark it
    // as answered by the visitor and defeat its own guard.
    this.addEventListener('change', (event) => {
      if (!event.isTrusted) return;

      const field = event.target instanceof Element ? event.target.closest('[data-field-name]') : null;
      const name = field instanceof HTMLElement ? field.dataset.fieldName : null;

      if (name) {
        this.#touched.add(name);
        field?.removeAttribute('data-preselected');
        if (name === 'enquiry_branch') this.#branchSource = 'asked';
      }
    });

    // Typing a quantity makes it the visitor's, so it stops following the headcount.
    this.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.dataset.variantId) return;

      this.#quantityDrafts.set(input.dataset.variantId, input.value);
      this.#quantitySeeded.delete(input.dataset.variantId);
    });

    // The shortlist can change underneath the flow: an item removed from the drawer's list, or the
    // last one removed, which takes the quantities step out of the sequence altogether.
    this.#onShortlistChange = () => {
      const step = this.#currentStep;
      if (step && this.#isQuantitiesStep(step)) this.#renderQuantities(step);
      this.#render();
    };
    document.addEventListener('shortlist:change', this.#onShortlistChange);

    // A start is the first time someone engages with the flow, not the flow rendering: it is
    // embedded in the footer's drawer, so on render it would fire on every page view and the
    // funnel's first step would just be page views (19 Oct 2026, found once it reached GA4).
    const start = (/** @type {Event} */ event) => {
      if (!event.isTrusted) return;
      for (const type of ['pointerdown', 'keydown', 'focusin']) this.removeEventListener(type, start);
      this.#push(EVENTS.enquiryStart, {
        route: this.#route,
        page_context: this.#pageContext,
      });
    };
    for (const type of ['pointerdown', 'keydown', 'focusin']) this.addEventListener(type, start);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#onShortlistChange) document.removeEventListener('shortlist:change', this.#onShortlistChange);
    this.#onShortlistChange = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindOptionInputs();
    this.#render();
  }

  /**
   * Assigns `name` and `required` to option inputs from their containing `[data-field]`.
   *
   * A Horizon block cannot read its parent block's settings in Liquid, so an option block does
   * not know which question it belongs to. Binding here keeps the option knowing only its own
   * value, which is the right split anyway — the same option markup works under a step or a
   * secondary question without either knowing about the other.
   *
   * @returns {void}
   */
  #bindOptionInputs() {
    for (const field of this.querySelectorAll('[data-field][data-field-name]')) {
      if (!(field instanceof HTMLElement)) continue;

      const name = field.dataset.fieldName;
      if (!name) continue;

      const required = field.hasAttribute('data-required');

      for (const input of field.querySelectorAll('[data-option-input]')) {
        if (!(input instanceof HTMLInputElement)) continue;

        input.name = name;
        input.required = required;
      }
    }
  }

  /**
   * Advances to the next step, or submits when there are none left.
   *
   * @returns {void}
   */
  /**
   * Fills in an answer the visitor has not given yet, without taking the choice away.
   *
   * Used by the shortlist drawer to pre-select the enquiry branch when what someone saved makes
   * the answer unambiguous: only catalogue items means "I know what I need", only past work means
   * "show me what's possible". A mixed selection is asked, not guessed (see `clearPreselect`).
   * §4 of the lead capture brief calls screen one the single most valuable question in the
   * system, which is exactly why a guess is recorded as one (`enquiry_branch_source`).
   *
   * **Pre-select, never lock** (§9). The step still renders, every option stays selectable, and
   * once the visitor has touched the field themselves this stops overriding them — re-opening the
   * drawer must not quietly undo a deliberate choice.
   *
   * @param {string} field - Field name, e.g. 'enquiry_branch'
   * @param {string} value
   * @returns {void}
   */
  preselect(field, value) {
    if (!field || !value) return;
    if (this.#touched.has(field)) return;

    const input = this.querySelector(
      `[data-field-name="${field}"] input[value="${CSS.escape(value)}"]`
    );
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    input.checked = true;

    // Dispatched so the flow's own change handling records the answer and recomputes the branch,
    // exactly as it would for a click. Setting `.checked` alone fires nothing.
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Mark the field as pre-filled rather than answered, so a visible hint can say so.
    input.closest('[data-field-name]')?.setAttribute('data-preselected', '');

    // Lift screen one out of the sequence. It has been answered by behaviour, so re-asking it is a
    // step spent on a question the person has already answered.
    //
    // The answer has to be recorded here as well. `#branch` is normally derived in `next()`, after
    // collecting the step being left — and the whole point of absorbing screen one is that nobody
    // ever advances through it. Without this the branch stays null, only the shared steps are
    // visible, and the progress indicator falls back to its pre-branch estimate.
    if (field === 'enquiry_branch') {
      this.#answers[field] = value;
      this.#branchSource = 'inferred';

      const branch = BRANCH_KEYS[value] ?? null;
      if (branch !== this.#branch) {
        this.#branch = branch;
        this.#push(EVENTS.enquiryBranch, { route: this.#route, branch: value, source: 'inferred' });
      }

      this.#branchAbsorbed = true;
      this.#stepIndex = 0;
      this.#render();
    }
  }

  /**
   * Withdraws a pre-selection, so the question is asked after all.
   *
   * For when the evidence stops being unambiguous: someone whose saved items were all catalogue
   * adds a piece of past work and reopens the drawer. Leaves a visitor's own answer alone, and
   * does nothing to a field that was never pre-selected.
   *
   * @param {string} field
   * @returns {void}
   */
  clearPreselect(field) {
    if (!field || this.#touched.has(field)) return;
    if (field === 'enquiry_branch' && this.#branchSource !== 'inferred') return;

    const wrapper = this.querySelector(`[data-field-name="${field}"]`);
    if (!wrapper?.hasAttribute('data-preselected')) return;

    for (const input of wrapper.querySelectorAll('input:checked')) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }
    wrapper.removeAttribute('data-preselected');
    delete this.#answers[field];

    if (field === 'enquiry_branch') {
      this.#branch = null;
      this.#branchSource = null;
      this.#branchAbsorbed = false;
      this.#stepIndex = 0;
      this.#render();
    }
  }

  /**
   * Puts screen one back and goes to it.
   *
   * A real choice, not a confirmation — every option is selectable and nothing is pre-marked as
   * touched, so the visitor can pick either branch. Answers already given survive: they are keyed
   * by field name, and anything shared between the branches (contact details, and any step marked
   * `both`) is untouched by the change.
   *
   * @returns {void}
   */
  changeBranch() {
    // Counted, because an inference nobody checks is a rule running unchecked. If this fires on a
    // large share of inferred enquiries, the rule in shortlist-ui.js is wrong.
    this.#push(EVENTS.enquiryBranchChange, {
      route: this.#route,
      inferred_branch: this.#answers.enquiry_branch ?? null,
    });

    this.#branchAbsorbed = false;
    this.#render();

    const index = this.#visibleSteps.indexOf(/** @type {HTMLElement} */ (this.#branchStep));
    if (index >= 0) this.#goTo(index);
  }

  next() {
    const step = this.#currentStep;
    if (!step) return;

    if (!this.#validateStep(step)) return;

    this.#collect(step);

    const branchAnswer = this.#answers.enquiry_branch;
    if (branchAnswer && BRANCH_KEYS[branchAnswer]) {
      const branch = BRANCH_KEYS[branchAnswer] ?? null;

      if (branch !== this.#branch) {
        this.#branch = branch;
        this.#push(EVENTS.enquiryBranch, {
          route: this.#route,
          branch: branchAnswer,
          source: this.#branchSource ?? 'asked',
        });
      }
    }

    if (this.#isQuantitiesStep(step)) this.#saveQuantities(step);

    this.#push(EVENTS.enquiryStep, {
      route: this.#route,
      branch: this.#answers.enquiry_branch ?? null,
      step_name: step.dataset.stepName ?? '',
      step_index: this.#stepIndex + 1,
      step_duration_ms: Date.now() - this.#stepEnteredAt,
    });

    if (this.#stepIndex >= this.#visibleSteps.length - 1) {
      this.submit();
      return;
    }

    this.#goTo(this.#stepIndex + 1);
  }

  /**
   * Returns to the previous step. Answers are kept, so going back and forward loses nothing.
   *
   * @returns {void}
   */
  back() {
    if (this.#stepIndex === 0) return;
    this.#goTo(this.#stepIndex - 1);
  }

  /**
   * Clears a field when its "not sure yet" companion is ticked, and vice versa.
   *
   * Disabling rather than just blanking is the whole mechanism: `#validateStep` skips disabled
   * fields and `#collect` drops them, so a required question can carry an honest non-answer
   * without the step becoming optional and without a string reaching a Number property.
   *
   * The companion is named `<field>_unknown`, so the field it governs is found by name rather
   * than by input type — a step can carry a secondary question with inputs of its own.
   *
   * @param {Event} event
   * @returns {void}
   */
  toggleUnknown(event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    const name = checkbox.name.replace(/_unknown$/, '');
    if (name === checkbox.name) return;

    const step = checkbox.closest('[data-step-name]');
    const field = step?.querySelector(`[name="${CSS.escape(name)}"]`);
    if (!(field instanceof HTMLInputElement)) return;

    field.disabled = checkbox.checked;
    if (checkbox.checked) field.value = '';
    this.#clearFieldError(field);
  }

  /**
   * Submits the enquiry.
   *
   * @returns {Promise<void>}
   */
  async submit() {
    if (this.#submitting) return;

    // Honeypot. A real visitor never sees this field, so anything in it is a bot. Silently show
    // the confirmation rather than explaining what gave them away.
    if (this.refs.honeypot?.value) {
      this.#showPanel('confirmation');
      return;
    }

    // Timing gate. Nobody completes a multi-step form in under three seconds. This also protects
    // the audit endpoint, which is unauthenticated and metered.
    if (Date.now() - this.#startedAt < MIN_COMPLETION_MS) {
      this.#showPanel('confirmation');
      return;
    }

    this.#submitting = true;
    this.#setBusy(true);

    const fields = this.#buildFields();

    const result = await submitEnquiry({
      portalId: this.dataset.portalId ?? '',
      formGuid: this.dataset.formGuid ?? '',
      route: this.#route,
      fields,
      auditUrl: this.dataset.auditUrl || undefined,
      consentToProcess: this.#answers.marketing_consent === 'true',
      consentText: this.dataset.consentText || undefined,
      subscriptionTypeId: Number(this.dataset.subscriptionTypeId) || undefined,
    });

    this.#submitting = false;
    this.#setBusy(false);

    if (result.ok) {
      this.#push(EVENTS.enquirySubmit, {
        route: this.#route,
        branch: this.#answers.enquiry_branch ?? null,
        branch_source: this.#answers.enquiry_branch ? (this.#branchSource ?? 'asked') : null,
        occasion: this.#answers.occasion ?? null,
        vertical: this.#answers.vertical ?? null,
        budget_band: this.#answers.budget_band ?? null,
        engagement_type: this.#answers.engagement_type ?? null,
        item_count: Number(fields.item_count ?? 0),
      });

      // The Google Ads conversion. GA4 builds `flow_submitted` from a page view of the old thank-you
      // page; see assets/enquiry-conversion.js. Live theme only.
      reportEnquiryConversion({
        measurementId: this.dataset.ga4Id,
        conversionUrl: this.dataset.ga4ConversionUrl,
        route: this.#route,
      });

      this.#renderSummary();
      this.#showPanel('confirmation');
      return;
    }

    if (result.queued) {
      // Held for replay. The visitor is told honestly that it hasn't gone through yet — never a
      // false success — but the lead is not lost, and this event is how you find out it happened.
      this.#push(EVENTS.enquirySubmitDeferred, {
        route: this.#route,
        attempt: 1,
        reason: result.status === 0 ? 'network' : String(result.status),
      });
    }

    this.#showFailure(fields);
  }

  /** @returns {string} */
  get #route() {
    return this.dataset.route ?? 'questionnaire';
  }

  /**
   * Where the enquiry started, as posted to HubSpot's `page_context` field.
   *
   * Two parameter names, because two things send people here and neither is a collection in
   * the other's sense. `collection` is the older one and stays first so existing links keep
   * their meaning; `context` is the general form, used by the tier comparison to record which
   * package card someone clicked. That is budget intent expressed before the form asks for it.
   *
   * @returns {string}
   */
  get #pageContext() {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('collection') || params.get('context');
    return fromQuery || this.dataset.pageContext || '';
  }

  /**
   * Steps that apply to the branch currently chosen. Before screen one is answered, only shared
   * steps are visible.
   *
   * @returns {HTMLElement[]}
   */
  get #visibleSteps() {
    return this.refs.steps.filter((step) => {
      if (this.#branchAbsorbed && step === this.#branchStep) return false;
      if (this.#isQuantitiesStep(step) && this.#savedItems().length === 0) return false;

      const branch = step.dataset.branch || 'both';
      return branch === 'both' || branch === this.#branch;
    });
  }

  /**
   * The step that asks screen one, if this flow has one.
   *
   * @returns {HTMLElement | null}
   */
  get #branchStep() {
    const field = this.querySelector('[data-field-name="enquiry_branch"]');
    return field instanceof HTMLElement ? field.closest('[data-branch], .flow-step') : null;
  }

  /** @returns {HTMLElement | undefined} */
  get #currentStep() {
    return this.#visibleSteps[this.#stepIndex];
  }

  /**
   * @param {number} index
   * @returns {void}
   */
  #goTo(index) {
    this.#stepIndex = index;
    this.#stepEnteredAt = Date.now();
    this.#render();

    const step = this.#currentStep;
    if (!step) return;

    if (this.#isQuantitiesStep(step)) this.#renderQuantities(step);

    const heading = step.querySelector('[data-step-heading]');

    // Move focus to the new step's heading so a screen reader announces the question rather than
    // leaving focus on a Continue button that has just moved.
    if (heading instanceof HTMLElement) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }

    this.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  /**
   * @returns {void}
   */
  #render() {
    const visible = this.#visibleSteps;

    for (const step of this.refs.steps) {
      step.hidden = step !== visible[this.#stepIndex];
    }

    this.#renderBranchLine();

    const { backButton, nextButton, progressBar, progressText } = this.refs;

    if (backButton) backButton.hidden = this.#stepIndex === 0;

    if (nextButton) {
      const isLast = this.#stepIndex >= visible.length - 1;
      nextButton.textContent = isLast
        ? nextButton.dataset.labelSubmit || nextButton.textContent
        : nextButton.dataset.labelNext || nextButton.textContent;
    }

    const position = this.#stepIndex + 1;
    const total = Math.max(this.#estimatedTotal(visible), position);

    if (progressBar) {
      progressBar.style.setProperty('--enquiry-progress', `${(position / total) * 100}%`);
      progressBar.setAttribute('aria-valuenow', String(position));
      progressBar.setAttribute('aria-valuemax', String(total));
    }

    if (progressText) {
      const template = progressText.dataset.template || '[current] / [total]';
      progressText.textContent = template.replace('[current]', String(position)).replace('[total]', String(total));
    }
  }

  /**
   * How many steps to promise in the progress indicator.
   *
   * Before screen one is answered only the shared steps are visible, so counting those would say
   * "1 of 2" and then jump to "2 of 5" the moment a branch is chosen. An indicator that revises
   * upwards reads as a bait-and-switch, and it would do it on the screen where 65% of visitors
   * already leave within nine seconds.
   *
   * So until the branch is known, assume the longest branch. The count then only ever revises
   * *down*, which is a pleasant surprise rather than a broken promise.
   *
   * @param {HTMLElement[]} visible
   * @returns {number}
   */
  #estimatedTotal(visible) {
    if (this.#branch !== null) return visible.length;

    let shared = 0;
    /** @type {Record<string, number>} */
    const perBranch = {};

    for (const step of this.refs.steps) {
      if (this.#isQuantitiesStep(step) && this.#savedItems().length === 0) continue;

      const branch = step.dataset.branch || 'both';

      if (branch === 'both') {
        shared++;
        continue;
      }

      perBranch[branch] = (perBranch[branch] ?? 0) + 1;
    }

    const longest = Object.values(perBranch).reduce((max, count) => Math.max(max, count), 0);

    return shared + longest;
  }

  /**
   * Reads every named field in a step into the answer set.
   *
   * @param {HTMLElement} step
   * @returns {void}
   */
  #collect(step) {
    for (const field of this.#fieldsIn(step)) {
      const name = field.name;
      if (!name) continue;

      // Quantities belong to the shortlist, not to the answer set. See `#saveQuantities`.
      if (field instanceof HTMLInputElement && field.dataset.variantId) continue;

      if (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox')) {
        if (field.type === 'radio') {
          if (field.checked) this.#answers[name] = field.value;
          continue;
        }

        // A ticked checkbox with a value is a real answer; without one it is a boolean.
        if (field.checked) {
          this.#answers[name] = field.value && field.value !== 'on' ? field.value : 'true';
        } else {
          delete this.#answers[name];
        }
        continue;
      }

      if (field.disabled || field.value === '') {
        delete this.#answers[name];
        continue;
      }

      this.#answers[name] = field.value;
    }
  }

  /**
   * @param {HTMLElement} step
   * @returns {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]}
   */
  #fieldsIn(step) {
    /** @type {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]} */
    const fields = [];

    for (const field of step.querySelectorAll('input[name], select[name], textarea[name]')) {
      if (field === this.refs.honeypot) continue;

      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
      ) {
        fields.push(field);
      }
    }

    return fields;
  }

  /**
   * Validates a step, showing errors adjacent to each field, moving focus to the first one,
   * announcing it, and scrolling it into view.
   *
   * @param {HTMLElement} step
   * @returns {boolean}
   */
  #validateStep(step) {
    this.#clearStepErrors(step);

    /** @type {{ field: HTMLElement, message: string, name: string } | null} */
    let firstError = null;

    const radioGroupsSeen = new Set();

    for (const field of this.#fieldsIn(step)) {
      if (field.disabled) continue;

      const isRadio = field instanceof HTMLInputElement && field.type === 'radio';

      if (isRadio) {
        if (radioGroupsSeen.has(field.name)) continue;
        radioGroupsSeen.add(field.name);

        const group = step.querySelectorAll(`input[type="radio"][name="${CSS.escape(field.name)}"]`);
        const required = Array.from(group).some((input) => input instanceof HTMLInputElement && input.required);
        const answered = Array.from(group).some((input) => input instanceof HTMLInputElement && input.checked);

        if (required && !answered) {
          const message = this.#messageFor(field, 'select');
          this.#showFieldError(field, message);
          firstError ??= { field, message, name: field.name };
        }
        continue;
      }

      if (field.required && field.value.trim() === '') {
        const message = this.#messageFor(field, 'enter');
        this.#showFieldError(field, message);
        firstError ??= { field, message, name: field.name };
        continue;
      }

      // Let the browser handle format checking — type="email", type="number", min, max, pattern.
      if (field.value !== '' && !field.checkValidity()) {
        const message = this.#messageFor(field, 'invalid');
        this.#showFieldError(field, message);
        firstError ??= { field, message, name: field.name };
      }
    }

    if (!firstError) return true;

    this.#push(EVENTS.enquiryError, {
      route: this.#route,
      branch: this.#answers.enquiry_branch ?? null,
      step_name: step.dataset.stepName ?? '',
      field: firstError.name,
      message: firstError.message,
    });

    this.#announce(firstError.message);

    // The fix. Scroll first, then focus without letting the browser scroll again — the current
    // form does neither, which is why its error lands offscreen.
    firstError.field.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });
    firstError.field.focus({ preventScroll: true });

    return false;
  }

  /**
   * Builds an error message from the field's own label, so it can never name a different field.
   *
   * @param {HTMLElement} field
   * @param {'enter' | 'select' | 'invalid'} kind
   * @returns {string}
   */
  #messageFor(field, kind) {
    const override = field.getAttribute('data-error-message');
    if (override) return override;

    const label = this.#labelFor(field);
    const templates = window.Theme?.translations ?? {};

    const template =
      kind === 'select'
        ? templates.enquiry_error_select || 'Please choose an option for [field].'
        : kind === 'invalid'
          ? templates.enquiry_error_invalid || 'Please check [field].'
          : templates.enquiry_error_required || 'Please enter [field].';

    // A label phrased as a question ("Where are you based?") reads badly inside "Please enter …."
    // It is quoted whole instead, keeping its own question mark.
    if (kind !== 'invalid' && /\?\s*$/.test(label)) {
      return (templates.enquiry_error_question || 'Please answer: [field]').replace('[field]', label);
    }

    return template.replace('[field]', label);
  }

  /**
   * @param {HTMLElement} field
   * @returns {string}
   */
  #labelFor(field) {
    const wrapper = field.closest('[data-field]');
    const explicit = wrapper?.getAttribute('data-field-label');
    if (explicit) return explicit.toLowerCase();

    const labelled = wrapper?.querySelector('label, legend');
    return (labelled?.textContent ?? 'this').trim().replace(/\s*\*$/, '').toLowerCase();
  }

  /**
   * @param {HTMLElement} field
   * @param {string} message
   * @returns {void}
   */
  #showFieldError(field, message) {
    const wrapper = field.closest('[data-field]');
    const container = wrapper?.querySelector('[data-field-error]');

    if (container instanceof HTMLElement) {
      container.textContent = message;
      container.hidden = false;

      const id = container.id || `${field.getAttribute('name') ?? 'field'}-error-${this.dataset.blockId ?? 'flow'}`;
      container.id = id;
      field.setAttribute('aria-describedby', id);
    }

    field.setAttribute('aria-invalid', 'true');
  }

  /**
   * @param {HTMLElement} field
   * @returns {void}
   */
  #clearFieldError(field) {
    const wrapper = field.closest('[data-field]');
    const container = wrapper?.querySelector('[data-field-error]');

    if (container instanceof HTMLElement) {
      container.textContent = '';
      container.hidden = true;
    }

    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
  }

  /**
   * @param {HTMLElement} step
   * @returns {void}
   */
  #clearStepErrors(step) {
    for (const field of this.#fieldsIn(step)) {
      this.#clearFieldError(field);
    }
  }

  /**
   * @param {string} message
   * @returns {void}
   */
  #announce(message) {
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = message;
  }

  /**
   * Assembles the HubSpot payload. Keys are internal property names and must match the forms
   * exactly — HubSpot returns 200 while silently dropping fields it does not recognise.
   *
   * @returns {Record<string, string>}
   */
  #buildFields() {
    /** @type {Record<string, string>} */
    const fields = {
      ...this.#answers,
      ...EnquiryAttribution.payload(),
      enquiry_route: this.#route,
    };

    if (this.#pageContext) fields.page_context = this.#pageContext;

    // Whether the branch was a guess. Without this, an inferred branch reaches HubSpot looking
    // exactly like a chosen one, and nobody can check whether the inference is any good.
    if (fields.enquiry_branch) fields.enquiry_branch_source = this.#branchSource ?? 'asked';
    if (isTestSubmission()) fields.is_test = 'true';

    // The shortlist seam. The flow reads from the store at submit time; the store knows nothing
    // about any form, so adding a fifth route needs no shortlist change. Absent until PR 4, hence
    // the guard — an enquiry with no saved items is the normal case, not an error.
    const shortlist = window.Shortlist;
    const payload = shortlist?.payload?.();

    if (payload && payload.item_count > 0) {
      fields.item_count = String(payload.item_count);

      // Prefer the grouped rendering. Five undifferentiated line items in an account manager's
      // inbox is the "Quantity: Not Given" failure in a new costume — "three items to quote, two
      // references for direction" is a brief they can act on. Falls back to the flat summary so a
      // cached older store still submits something usable.
      fields.shortlist_items = payload.summary_grouped || payload.summary;

      if (payload.item_handles) fields.item_handles = payload.item_handles;
      if (payload.bespoke_count) fields.bespoke_count = String(payload.bespoke_count);
      if (payload.core_count) fields.core_count = String(payload.core_count);

      // Branch A asks "how many?" as one number, which is the whole enquiry's volume.
      if (!fields.total_units && payload.total_units) fields.total_units = String(payload.total_units);
      if (!fields.primary_item && payload.primary_item) fields.primary_item = payload.primary_item;
    } else {
      fields.item_count = '0';
    }

    // Consent is expressed through legalConsentOptions, not as a property.
    delete fields.marketing_consent;

    // "Not sure yet" companions are UI state, not data. HubSpot would accept them with a 200 and
    // silently discard them, which is the failure mode worth being fussy about.
    for (const key of Object.keys(fields)) {
      if (key.endsWith('_unknown')) delete fields[key];
    }

    return fields;
  }

  /**
   * Restates what they told us. Brief §4: do not show a generic thank-you.
   *
   * The person who will be in touch is named in the HubSpot acknowledgement email rather than
   * here, because owner assignment happens after this POST returns and duplicating the rota in
   * the theme would mean it silently names the wrong person the first time it changes.
   *
   * @returns {void}
   */
  #renderSummary() {
    const { summary } = this.refs;
    if (!summary) return;

    for (const row of summary.querySelectorAll('[data-summary-for]')) {
      if (!(row instanceof HTMLElement)) continue;

      const name = row.dataset.summaryFor ?? '';
      const value = this.#answers[name];
      const target = row.querySelector('[data-summary-value]');

      if (!value) {
        row.hidden = true;
        continue;
      }

      row.hidden = false;

      // Show the label the visitor chose, not the handle sent to HubSpot.
      if (target instanceof HTMLElement) target.textContent = this.#displayValue(name, value);
    }

    const firstName = this.#answers.firstname;
    for (const slot of summary.querySelectorAll('[data-summary-name]')) {
      if (slot instanceof HTMLElement) slot.textContent = firstName ?? '';
    }
  }

  /**
   * @param {string} name
   * @param {string} value
   * @returns {string}
   */
  #displayValue(name, value) {
    const option = this.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
    const wrapper = option?.closest('[data-option]');
    const label = wrapper?.querySelector('[data-option-label]');

    if (label?.textContent) return label.textContent.trim();

    if (option instanceof HTMLOptionElement) return option.textContent?.trim() ?? value;

    return value;
  }

  /**
   * Honest failure. Their answers stay on screen, and the mailto carries them so the enquiry can
   * still reach a human. Never replace a dead button with a lying one.
   *
   * @param {Record<string, string>} fields
   * @returns {void}
   */
  #showFailure(fields) {
    const { mailtoFallback } = this.refs;

    if (mailtoFallback) {
      const address = mailtoFallback.dataset.address || '';
      const subject = mailtoFallback.dataset.subject || 'Enquiry';
      const body = Object.entries(fields)
        .filter(([key]) => !key.startsWith('first_touch') && !key.startsWith('last_touch') && key !== 'click_ids')
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');

      mailtoFallback.href = `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }

    this.#showPanel('failure');
  }

  /**
   * @param {'confirmation' | 'failure'} panel
   * @returns {void}
   */
  #showPanel(panel) {
    for (const step of this.refs.steps) step.hidden = true;

    if (this.refs.controls) this.refs.controls.hidden = true;
    if (this.refs.confirmation) this.refs.confirmation.hidden = panel !== 'confirmation';
    if (this.refs.failure) this.refs.failure.hidden = panel !== 'failure';

    const target = panel === 'confirmation' ? this.refs.confirmation : this.refs.failure;

    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  /**
   * @param {boolean} busy
   * @returns {void}
   */
  #setBusy(busy) {
    const { nextButton } = this.refs;
    if (!nextButton) return;

    nextButton.disabled = busy;
    nextButton.setAttribute('aria-busy', String(busy));
  }

  /**
   * @param {HTMLElement} step
   * @returns {boolean}
   */
  #isQuantitiesStep(step) {
    return step.hasAttribute('data-shortlist-quantities');
  }

  /**
   * The shortlist as the flow sees it. Read through the global rather than imported, so the flow
   * still works on a page that never loads the shortlist; that page simply has no saved items.
   *
   * @returns {ShortlistItem[]}
   */
  #savedItems() {
    return window.Shortlist?.items?.() ?? [];
  }

  /**
   * The number every quantity starts from: the headcount asked on this branch. Branch A asks
   * "how many?", branch B asks "how many people?", and each has its own quantities step straight
   * after, so whichever the visitor has just answered is the one to use. Blank when they said
   * "not sure yet", which leaves every quantity blank too.
   *
   * @param {HTMLElement} step
   * @returns {string}
   */
  #quantitySeed(step) {
    const { total_units: units, recipient_count: people } = this.#answers;
    return (step.dataset.branch === 'a' ? units ?? people : people ?? units) ?? '';
  }

  /**
   * One row per saved item, whatever its kind. Every item gets a box: hiding some would show the
   * catalogue and past-work split as a gap in the list, and "something like this, for 200 people"
   * is a usable brief on past work too.
   *
   * @param {HTMLElement} step
   * @returns {void}
   */
  #renderQuantities(step) {
    const list = step.querySelector('[data-quantity-list]');
    if (!(list instanceof HTMLElement)) return;

    const seed = this.#quantitySeed(step);
    const labels = {
      item: list.dataset.labelItem || 'Quantity for [title]',
      invalid: list.dataset.labelInvalid || 'Use a whole number for [title], or leave it blank.',
    };
    const rows = [];

    for (const item of this.#savedItems()) {
      const id = item.variantId;
      let value = this.#quantityDrafts.get(id);

      if (value === undefined) {
        if (item.quantity !== null && !this.#quantitySeeded.has(id)) {
          value = String(item.quantity);
        } else {
          value = seed;
          this.#quantitySeeded.add(id);
        }
      }

      rows.push(this.#quantityRow(item, value, labels));
    }

    list.replaceChildren(...rows);
  }

  /**
   * @param {ShortlistItem} item
   * @param {string} value
   * @param {{ item: string, invalid: string }} labels
   * @returns {HTMLLIElement}
   */
  #quantityRow(item, value, labels) {
    // Product title only, as in the drawer: nobody chose the variant.
    const title = item.productTitle || item.variantTitle;
    const id = `flow-quantity-${this.dataset.blockId ?? 'flow'}-${item.variantId}`;

    const row = document.createElement('li');
    row.className = 'flow-quantities__item';
    row.setAttribute('data-field', '');
    row.dataset.fieldLabel = title;

    const label = document.createElement('label');
    label.className = 'flow-quantities__title';
    label.htmlFor = id;
    label.textContent = title;

    // Visible label is the product; the accessible name says what the number is for.
    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'flow-field__input flow-quantities__input';
    input.id = id;
    input.name = `shortlist_quantity_${item.variantId}`;
    input.inputMode = 'numeric';
    input.min = '1';
    input.step = '1';
    input.value = value;
    input.dataset.variantId = item.variantId;
    input.setAttribute('aria-label', labels.item.replace('[title]', title));

    // Its own message: the generic one lowercases the field label, which mangles product names,
    // and "please check" would not say that blank is allowed.
    input.setAttribute('data-error-message', labels.invalid.replace('[title]', title));

    const error = document.createElement('p');
    error.className = 'flow-field__error';
    error.setAttribute('data-field-error', '');
    error.hidden = true;

    row.append(label, input, error);
    return row;
  }

  /**
   * Writes the step's quantities to the shortlist, so they travel with it to any form and are
   * there if the visitor comes back. Blank clears; validation has already ruled out anything that
   * is not a positive whole number.
   *
   * @param {HTMLElement} step
   * @returns {void}
   */
  #saveQuantities(step) {
    /** @type {Record<string, number | null>} */
    const quantities = {};

    for (const input of step.querySelectorAll('input[data-variant-id]')) {
      if (!(input instanceof HTMLInputElement) || !input.dataset.variantId) continue;
      quantities[input.dataset.variantId] = input.value === '' ? null : Number(input.value);
    }

    window.Shortlist?.setQuantities?.(quantities);
  }

  /**
   * Shows the absorbed branch answer as a line, using the option's own label.
   *
   * @returns {void}
   */
  #renderBranchLine() {
    const { branchSummary, branchLabel } = this.refs;
    if (!branchSummary || !branchLabel) return;

    branchSummary.hidden = !this.#branchAbsorbed;
    if (!this.#branchAbsorbed) return;

    const checked = this.querySelector('[data-field-name="enquiry_branch"] input:checked');
    const wrapper = checked instanceof HTMLElement ? checked.closest('[data-option]') : null;
    const label = wrapper?.querySelector('[data-option-label]')?.textContent?.trim();

    branchLabel.textContent = label ?? '';
  }

  /**
   * Pushes an analytics event to the dataLayer, where GTM forwards it to GA4.
   *
   * Event names are fixed by brief §8 — renaming one breaks reporting silently — so they come
   * from the frozen `EVENTS` map in `@theme/analytics` rather than being written here.
   *
   * @param {string} event
   * @param {Record<string, unknown>} params
   * @returns {void}
   */
  #push(event, params) {
    push(event, params);
  }
}

if (!customElements.get('enquiry-flow')) {
  customElements.define('enquiry-flow', EnquiryFlow);
}
